import React, { useContext, useEffect, useState } from 'react';

import { OneOnOneInstanceContext as DeerOneOnOneInstanceContext } from 'Deer/pages/MyData/OneOnOne/LegacyInstancePage/LegacyInstancePage';
import { createCommentApi, editCommentApi } from 'api/post';
import SkeletonLoader from 'components/SkeletonLoader';
import FeedLoadingView from 'components/feed/FeedLoadingView';
import useAsync from 'components/hooks/useAsync';
import useForceUpdate from 'components/hooks/useForceUpdate';
import { useMatchedRoute } from 'components/hooks/useMatchedRoute';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { useSelector, shallowEqual } from 'react-redux';
import { OneOnOneInstanceContext } from 'screens/oneOnOne/instancePage/InstancePageContext';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import {
  StyledDivider,
  StyledGrid,
  StyledTooltip,
  StyledPaper,
  StyledTypography,
  SpaceBetweenContainer,
  VerticalFlexContainer,
} from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { ONE_ON_ONE_STATUSES } from 'constants/ProjectConstants';
import { hyperlinkUrlsInText } from 'constants/helper';
import { LockIcon, StyledIcon, TwoPeopleIcon } from 'constants/icons';
import { ONE_ON_ONE_EVENTS_OLD, sendOneOnOneEvent } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { getCheckInQuestions } from 'services/OneOnOne';
// for the emoji type question
import { getTalkingPointComments } from 'services/task';

import EmojiPopupSelect from 'uiToolkit/EmojiPopupSelect';

import Agenda from '../Agenda';
import TalkingPointDiscussion from './OneOnOneTalkingPointDiscussion';
import TalkingPointPanel from './TalkingPointPanel';

export const TALKING_POINT_ANSWER_TYPES = {
  EMOJI: 'EMOJI',
  SHORT_TEXT: 'SHORT_TEXT',
  CHECK_BOX: 'CHECK_BOX',
};

export const TALKING_POINT_QUESTION_TYPES = {
  QNA: 'QNA',
  EMOTION: 'EMOTION',
  MEETING_NOTE: 'MEETING_NOTE',
  AGENDA_ITEM: 'AGENDA_ITEM',
};

const TalkingPointContainer = styled(SpaceBetweenContainer)`
  padding: 8px;
  border-radius: 4px;
  ${(props) =>
    props.showErrorBorder &&
    css`
      border: 1px solid red;
    `}
`;

const TalkingPoints = ({
  oneOnOneInstanceId,
  isSubmitted,
  isManager,
  setEnableMarkAsComplete,
  clickedMarkAsComplete,
  taskDetails,
}) => {
  const contextToUse = checkNewNavEnabled() ? DeerOneOnOneInstanceContext : OneOnOneInstanceContext;

  const { data: checkInQuestions, isLoading, isIdle, run } = useAsync();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const [isOpenSidePanel, setOpenSidePanel] = useState(false);
  const [selectedTalkingPointIndex, setSelectedTalkingPointIndex] = useState(-1);
  const [forceUpdateValue, forceUpdate] = useForceUpdate();

  const [requiredUnAnsweredTalkingPointsMap, setRequiredUnAnsweredTalkingPointsMap] = useState({});

  const closeSidePanel = () => {
    setOpenSidePanel(false);
  };

  const openSidePanel = (idx) => {
    setSelectedTalkingPointIndex(idx);
    setOpenSidePanel(true);
  };

  const {
    state: {
      otherEmployee: otherMember,
      timelineData,
      instanceDetails: { active },
    },
  } = useContext(contextToUse);

  const isActiveTask = active;

  useEffect(() => {
    run(getCheckInQuestions({ checkInId: oneOnOneInstanceId, orgId: loggedInEmployee?.organization?.id }));
  }, [oneOnOneInstanceId, loggedInEmployee?.organization?.id, run, forceUpdateValue]);

  useEffect(() => {
    if (checkInQuestions && checkInQuestions?.length) {
      const requiredUnAnsweredQuesIds = checkInQuestions
        .filter((ques) => ques.required && ques.totalCommentCount === 0)
        .map((ques) => ques.uuid);

      if (requiredUnAnsweredQuesIds.length === 0) {
        setEnableMarkAsComplete(true);
      }

      setRequiredUnAnsweredTalkingPointsMap(
        requiredUnAnsweredQuesIds.reduce((acc, quesId) => ({ ...acc, [quesId]: false }), {})
      );
    }
  }, [checkInQuestions, checkInQuestions?.length]);

  const markRequiredQuestionAnswered = (quesUuid) => {
    setRequiredUnAnsweredTalkingPointsMap((prev) => {
      const updatedState = { ...prev, [quesUuid]: true };

      const areAllRequiredQuestionsAnswered = Object.values(updatedState).reduce((rVal, cVal) => rVal && cVal, true);
      if (areAllRequiredQuestionsAnswered) setEnableMarkAsComplete(true);
      return updatedState;
    });
  };

  if (isLoading || isIdle) {
    return <FeedLoadingView />;
  }

  if (checkInQuestions.length > 0 || selectedTalkingPointIndex >= 0) {
    return (
      <StyledPaper elevation={0} showBorder padding="1rem" bottomGutter={2}>
        <div>
          {checkInQuestions.map((talkingPoint, idx) => {
            const isAnswered =
              requiredUnAnsweredTalkingPointsMap[talkingPoint.uuid] === true ||
              requiredUnAnsweredTalkingPointsMap[talkingPoint.uuid] === undefined;
            return (
              <>
                <TalkingPointContainer key={talkingPoint.uuid} showErrorBorder={clickedMarkAsComplete && !isAnswered}>
                  <TalkingPoint
                    talkingPoint={talkingPoint}
                    oneOnOneInstanceId={oneOnOneInstanceId}
                    showTalkingPointsPanel={() => {
                      openSidePanel(idx);
                    }}
                    isSubmitted={isSubmitted}
                    isHigherManager={isManager}
                    isAnswered={isAnswered}
                    markRequiredQuestionAnswered={markRequiredQuestionAnswered}
                    taskDetails={taskDetails}
                    isActiveTask={isActiveTask}
                  />
                </TalkingPointContainer>
                {idx !== checkInQuestions.length - 1 && (
                  <StyledDivider topGutter={1} bottomGutter={2} leftGutter={0.5} rightGutter={0.5} />
                )}
              </>
            );
          })}
          {selectedTalkingPointIndex >= 0 && (
            <TalkingPointPanel
              forceUpdateParent={forceUpdate}
              open={checkInQuestions[selectedTalkingPointIndex] && isOpenSidePanel}
              talkingPoint={checkInQuestions[selectedTalkingPointIndex]}
              onCancel={closeSidePanel}
              checkInId={oneOnOneInstanceId}
              currentCheckInInstanceId={oneOnOneInstanceId}
              otherMember={otherMember}
              latestCheckInInstance={timelineData[0]}
              parentTaskId={taskDetails.parentTaskId}
              isActiveTask={isActiveTask}
            />
          )}
        </div>
      </StyledPaper>
    );
  }
  return null;
};

const EmojiTalkingPoint = ({
  talkingPointQuestionId,
  oneOnOneInstanceId,
  editable = true,
  tooltipText = '',
  options,
  isPredefined,
  isRequired,
  isAnswered,
  markRequiredQuestionAnswered,
}) => {
  // undefined means, hasn't fetched the prev value yet
  const [selectedEmojiScore, setSelectedEmojiScore] = useState(undefined);
  const [commentId, setSelectedCommentId] = useState(null);

  const { pageName } = useMatchedRoute();

  const getPrevSavedVal = () => {
    getTalkingPointComments({
      checkInId: oneOnOneInstanceId,
      talkingPointQuestionId,
    }).then((res) => {
      if (res.length > 0) {
        setSelectedEmojiScore(parseInt(res?.[0]?.text));
        setSelectedCommentId(res?.[0]?.id);
      } else {
        setSelectedEmojiScore(null);
      }
    });
  };

  const onEmojiUpdate = (id) => {
    // need to supply this hardcoded as partial updates are not allowed
    const body = {
      text: id,
      media: [],
      // all emoji type answers should be global. ( as per current requirement)
      visibilityType: 'GLOBAL',
      macros: {
        employees: {},
        demographics: {},
        attributes: { orgTag: {}, money: null },
      },
    };
    if (commentId) {
      editCommentApi({
        feedId: oneOnOneInstanceId,
        commentBody: body,
        feedType: 'TASK',
        talkingPointQuestionId,
        commentId,
      }).then((res) => {});
    } else {
      createCommentApi({
        feedId: oneOnOneInstanceId,
        commentBody: body,
        feedType: 'TASK',
        talkingPointQuestionId,
      }).then((res) => {
        // cause we need to use POST method for first ever call and PUT after that
        // commentId is used to track of that
        if (isRequired && !isAnswered) markRequiredQuestionAnswered(talkingPointQuestionId);
        setSelectedCommentId(res?.entity?.comment?.post?.id);
      });
    }

    // Send Mark Complete or revertComplete
    sendOneOnOneEvent({
      eventName: ONE_ON_ONE_EVENTS_OLD.answeredTalkingPoints,
      page: {
        pageName,
        flow: 'Activity',
      },
      secondaryAction: ONE_ON_ONE_EVENTS_OLD.answeredTalkingPoints,
    });
  };

  useEffect(() => getPrevSavedVal(), []);

  if (selectedEmojiScore === undefined) {
    return <SkeletonLoader skeletons={['80%']} />;
  }
  return (
    <EmojiPopupSelect
      topGutter={1}
      bottomGutter={1}
      prevVal={selectedEmojiScore}
      onChange={onEmojiUpdate}
      editable={editable}
      tooltipText={tooltipText}
      options={options}
    />
  );
};

export const AgendaItemTalkingPoint = ({
  talkingPointQuestionId,
  // oneOnOneInstanceId,
  // editable,
  // tooltipText = '',
  // options,
  // isPredefined,
  // isRequired,
  // isAnswered,
  // markRequiredQuestionAnswered,
  // agendaItemsFromJSON,
  taskDetails,
  visibility,
  questionText,
  isSubmitted = false,
  isDisabled,
}) => {
  const contextToUse = checkNewNavEnabled() ? DeerOneOnOneInstanceContext : OneOnOneInstanceContext;

  const {
    state: {
      instanceDetails: { parentTaskId, active, checkInState },
    },
  } = useContext(contextToUse);
  const filterEmpIds = taskDetails?.toIds?.map((empItem) => empItem.id);

  // undefined means, hasn't fetched the prev value yet
  // const [agendaItems, setAgendaItems] = useState(undefined);
  // const [commentId, setSelectedCommentId] = useState(null);
  // const getPrevSavedVal = () => {
  //   const agendaItems = agendaItemsFromJSON.map(agenda => Task1.fromJSON(agenda));

  //   setAgendaItems(agendaItems);
  // };

  // useEffect(() => getPrevSavedVal(), []);

  // if (agendaItems === undefined || !taskDetails) {
  //   return <SkeletonLoader skeletons={['80%']} />;
  // }
  return (
    <Agenda
      header={questionText}
      taskId={taskDetails.id} // while creating children tasks in check in we need grandfather of it
      teamId={taskDetails.teamId}
      // setTaskDetails={setTaskDetails}
      isDisabled={
        !active ||
        checkInState === ONE_ON_ONE_STATUSES.CANCELLED ||
        checkInState === ONE_ON_ONE_STATUSES.ACKNOWLEDGED ||
        isDisabled
      }
      useMentionFunctionality
      subObjective={taskDetails.subObjective}
      parentTaskOwners={taskDetails.toIds}
      taskType={taskDetails.type}
      parentCheckInId={parentTaskId}
      filterEmpIds={filterEmpIds}
      // agendas={agendaItems}
      questionId={talkingPointQuestionId}
      visibility={visibility}
      isSubmitted={isSubmitted}
    />
  );
};

const TalkingPoint = ({
  talkingPoint,
  oneOnOneInstanceId,
  showTalkingPointsPanel,
  isSubmitted,
  isAnswered,
  markRequiredQuestionAnswered,
  isHigherManager,
  taskDetails,
  isActiveTask,
  isDisabled,
}) => {
  const {
    questionText,
    commentsUnReadCount,
    totalCommentCount,
    visibility,
    uuid,
    answerType,
    answerOptions,
    isPredefined,
    required,
    questionType,
  } = talkingPoint;

  const safeQuestionText = DOMPurify.sanitize(hyperlinkUrlsInText(questionText));

  return (
    <VerticalFlexContainer style={{ width: '100%' }}>
      {questionType !== TALKING_POINT_QUESTION_TYPES.AGENDA_ITEM && (
        <StyledGrid container alignItems="flex-start" wrap="nowrap">
          <StyledIcon size="tinier" topGutter={0.5} rightGutter={1} color={theme.palette.text.heading}>
            {visibility === 'PUBLIC' ? (
              <TwoPeopleIcon />
            ) : (
              <StyledTooltip title="Only you can see these comments">
                <LockIcon size="fixed12px" />
              </StyledTooltip>
            )}
          </StyledIcon>
          <StyledTypography variant="body2" weight={600} customColor={theme.palette.text.heading}>
            {parse(safeQuestionText)} {required ? '*' : ''}
          </StyledTypography>
        </StyledGrid>
      )}

      {answerType === TALKING_POINT_ANSWER_TYPES.EMOJI && (
        <EmojiTalkingPoint
          talkingPointQuestionId={uuid}
          // temp workaround till 'predefined' value form backend is fixed
          oneOnOneInstanceId={oneOnOneInstanceId}
          // editable={!isSubmitted && (!isPredefined || !isHigherManager)}
          editable={!isSubmitted && !isHigherManager && isActiveTask && !isDisabled}
          // tooltipText={
          //   !isSubmitted && isHigherManager && isPredefined ? 'To be answered by reportee' : ''
          // }
          tooltipText={!isSubmitted && isHigherManager ? 'To be answered by team member' : ''}
          options={answerOptions}
          isPredefined={isPredefined}
          isRequired={required}
          isAnswered={isAnswered}
          markRequiredQuestionAnswered={markRequiredQuestionAnswered}
        />
      )}
      {answerType === TALKING_POINT_ANSWER_TYPES.SHORT_TEXT && (
        <TalkingPointDiscussion
          oneOnOneInstanceId={oneOnOneInstanceId}
          talkingPointQuestionId={uuid}
          commentsUnReadCount={commentsUnReadCount}
          totalCommentCount={totalCommentCount}
          showTalkingPointsPanel={showTalkingPointsPanel}
          isSubmitted={isSubmitted}
          talkingPointQuestionVisibility={visibility}
          isRequired={required}
          isAnswered={isAnswered}
          markRequiredQuestionAnswered={markRequiredQuestionAnswered}
          isDisabled={!isActiveTask || isDisabled}
        />
      )}
      {questionType === TALKING_POINT_QUESTION_TYPES.AGENDA_ITEM && (
        <AgendaItemTalkingPoint
          questionText={questionText}
          talkingPointQuestionId={uuid}
          taskDetails={taskDetails}
          visibility={visibility}
          isSubmitted={isSubmitted}
          isDisabled={isDisabled}
          // oneOnOneInstanceId={oneOnOneInstanceId}
          // commentsUnReadCount={commentsUnReadCount}
          // totalCommentCount={totalCommentCount}
          // showTalkingPointsPanel={showTalkingPointsPanel}
          // isSubmitted={isSubmitted}
          // talkingPointQuestionVisibility={visibility}
          // isRequired={required}
          // isAnswered={isAnswered}
          // markRequiredQuestionAnswered={markRequiredQuestionAnswered}
          // editable={isActiveTask}
        />
      )}
    </VerticalFlexContainer>
  );
};

export default TalkingPoints;
